/* eslint-disable react-native/no-inline-styles */
import React, { FC, useMemo, Children } from 'react';
import {
    StyleSheet,
    TouchableHighlight,
    TouchableHighlightProps,
    Platform,
    View,
    StyleProp,
    ViewStyle,
    TextStyle,
} from 'react-native';

import colors from '@packages/core/styles/colors';

import { Typography } from './typography';
import { LoadingCircle } from './loading-circle';
import { useHoverState } from './hooks';

const styles = StyleSheet.create({
    base: {
        alignItems: 'center',
        flexDirection: 'row',
        borderRadius: 3,
        position: 'relative',
    },
    center: {
        justifyContent: 'center',
    },

    small: {
        height: 30,
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    medium: {
        height: 40,
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    large: {
        height: 48,
        paddingVertical: 12,
        paddingHorizontal: 24,
    },

    iconLeft: {
        marginRight: 8,
    },

    text: {
        fontSize: 15,
        fontWeight: '500',
        lineHeight: 24,
    },
    textLarge: {
        fontSize: 17,
    },
    textRegular: {
        fontWeight: '400',
    },
    textCaption: {
        fontSize: 13,
        lineHeight: 16,
    },
    textCenter: {
        textAlign: 'center',
    },

    iconRight: {
        marginLeft: 8,
    },

    whiteText: {
        color: colors.white,
    },
    linkHeaderText: {
        fontSize: 15,
    },
    darkText: {
        color: colors.textDarkPrimary,
    },
    blueText: {
        color: colors.blueOne,
    },
    grayText: {
        color: colors.textDarkSecondary,
    },
    linkText: {
        textDecorationLine: 'underline',
    },
    disabledText: {
        // color: '#08203e',
    },

    disabled: {
        opacity: 0.32,
    },

    primaryEnabled: {
        backgroundColor: colors.blueOne,
    },
    primaryHover: {
        backgroundColor: colors.blueOneHover,
    },

    secondaryEnabled: {
        backgroundColor: colors.brandSecondary,
    },
    secondaryHover: {
        backgroundColor: colors.brandSecondaryHover,
    },

    tertiary: {
        backgroundColor: colors.grayTwo,
    },

    dangerEnabled: {
        backgroundColor: colors.redOne,
    },
    dangerHover: {
        backgroundColor: colors.redOneHover,
    },

    transferEnabled: {
        backgroundColor: colors.whiteOne,
    },
    transferHover: {
        backgroundColor: colors.whiteOneHover,
    },

    successEnabled: {
        backgroundColor: colors.greenOne,
    },
    successHover: {
        backgroundColor: colors.greenOneHover,
    },
    primaryBlueOutlineHover: {
        backgroundColor: colors.blueFour,
        borderColor: colors.blueOne,
        borderWidth: 1,
    },
    primaryBlueOutlineActive: {
        backgroundColor: colors.blueThree,
        borderColor: colors.blueOne,
        borderWidth: 1,
    },
    ghost: {
        backgroundColor: 'transparent',
    },
    ghostBlueHover: {
        backgroundColor: colors.blueFour,
    },
    ghostBlueActive: {
        backgroundColor: colors.blueThree,
    },
    link: {
        backgroundColor: 'transparent',
        paddingVertical: 0,
        paddingHorizontal: 0,
    },
    headerLink: {
        backgroundColor: 'transparent',
    },
    block: {
        width: '100%',
    },
    blueLink: {
        color: colors.blueLink,
    },
    blueLinkHover: {
        color: colors.blueLinkHover,
    },
    outlinePrimaryEnabled: {
        backgroundColor: 'transparent',
        borderColor: colors.blueOne,
        borderWidth: 1,
    },
    outlinePrimaryHover: {
        backgroundColor: colors.blueOneHover,
        borderColor: colors.blueOne,
    },
    outlinePrimaryActive: {
        backgroundColor: colors.blueOneActive,
        borderColor: colors.blueOne,
    },
    tokenBlue: {
        backgroundColor: colors.blueFour,
        borderColor: colors.blueTwo,
        borderWidth: 1,
    },
    tokenBlueHover: {
        backgroundColor: colors.blueThree,
        borderColor: colors.blueTwo,
        borderWidth: 1,
    },
    tokenGray: {
        backgroundColor: colors.grayTwo,
        borderWidth: 1,
        borderColor: colors.grayThree,
    },
    tokenGrayHover: {
        backgroundColor: colors.grayThree,
        borderWidth: 1,
        borderColor: colors.grayFour,
    },
});

type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    | 'success'
    | 'primary-blue-outline'
    | 'ghost-blue'
    | 'ghost-gray'
    | 'link'
    | 'link-blue'
    | 'link-header-button'
    | 'info'
    | 'transfer'
    | 'outlinePrimary'
    | 'token-blue'
    | 'token-gray';

interface ButtonState {
    isHovered: boolean;
    isActive?: boolean;
}

const variantStyleMap = {
    primary: ({ isHovered, isActive }: ButtonState) => ({
        underlayColor: colors.blueOneActive,
        buttonStyle: isHovered || isActive ? styles.primaryHover : styles.primaryEnabled,
        textStyle: styles.whiteText,
    }),
    secondary: ({ isHovered }: ButtonState) => ({
        underlayColor: colors.brandSecondaryActive,
        buttonStyle: isHovered ? styles.secondaryHover : styles.secondaryEnabled,
        textStyle: styles.whiteText,
    }),
    tertiary: ({ isActive }: ButtonState) => ({
        underlayColor: colors.grayTwo,
        buttonStyle: isActive ? { backgroundColor: '#cbcbcb' } : styles.tertiary,
        textStyle: styles.darkText,
    }),
    danger: ({ isHovered }: ButtonState) => ({
        underlayColor: colors.redOneActive,
        buttonStyle: isHovered ? styles.dangerHover : styles.dangerEnabled,
        textStyle: styles.whiteText,
    }),
    transfer: ({ isHovered }: ButtonState) => ({
        underlayColor: colors.whiteOne,
        buttonStyle: isHovered ? styles.transferHover : styles.transferEnabled,
        textStyle: styles.darkText,
    }),
    success: ({ isHovered }: ButtonState) => ({
        underlayColor: colors.greenOneActive,
        buttonStyle: isHovered ? styles.successHover : styles.successEnabled,
        textStyle: styles.whiteText,
    }),
    'primary-blue-outline': ({ isHovered, isActive }: ButtonState) => ({
        underlayColor: '#cbcbcb',
        buttonStyle: isActive
            ? styles.primaryBlueOutlineActive
            : isHovered
              ? styles.primaryBlueOutlineHover
              : styles.outlinePrimaryEnabled,
        textStyle: styles.blueText,
    }),
    'ghost-blue': ({ isHovered, isActive }: ButtonState) => ({
        underlayColor: '#cbcbcb',
        buttonStyle: isActive ? styles.ghostBlueActive : isHovered ? styles.ghostBlueHover : styles.ghost,
        textStyle: styles.blueText,
    }),
    'ghost-gray': ({ isHovered }: ButtonState) => ({
        underlayColor: colors.white,
        buttonStyle: isHovered ? { backgroundColor: colors.grayTwo } : styles.ghost,
        textStyle: styles.grayText,
    }),
    link: () => ({
        underlayColor: colors.white,
        buttonStyle: styles.link,
        textStyle: [styles.blueText, styles.linkText],
    }),
    'link-blue': ({ isHovered }: ButtonState) => ({
        underlayColor: colors.white,
        buttonStyle: styles.link,
        textStyle: [styles.textRegular, isHovered ? styles.blueLinkHover : styles.blueLink],
    }),
    'link-header-button': () => ({
        underlayColor: 'transparent',
        buttonStyle: styles.headerLink,
        textStyle: [styles.whiteText, styles.linkHeaderText],
    }),
    info: () => ({
        underlayColor: '#dee6ef',
        buttonStyle: styles.ghostBlueHover,
        textStyle: styles.blueText,
    }),
    outlinePrimary: ({ isHovered, isActive }: ButtonState) => {
        const buttonStyle = () => {
            if (isActive) {
                return styles.outlinePrimaryActive;
            }
            if (isHovered) {
                return styles.outlinePrimaryHover;
            }

            return styles.outlinePrimaryEnabled;
        };

        const textStyle = isHovered ? styles.whiteText : styles.blueText;

        return {
            underlayColor: colors.blueOneActive,
            buttonStyle: buttonStyle(),
            textStyle: textStyle,
        };
    },
    'token-blue': ({ isHovered, isActive }: ButtonState) => ({
        underlayColor: colors.blueFour,
        buttonStyle: isHovered || isActive ? styles.tokenBlueHover : styles.tokenBlue,
        textStyle: [styles.textCaption, styles.blueText],
    }),
    'token-gray': ({ isHovered, isActive }: ButtonState) => ({
        underlayColor: colors.grayTwo,
        buttonStyle: isHovered || isActive ? styles.tokenGrayHover : styles.tokenGray,
        textStyle: [styles.textCaption, styles.textRegular, styles.grayText],
    }),
} as const;

interface ButtonProps extends TouchableHighlightProps {
    title?: string | JSX.Element;
    alignTitle?: 'left' | 'center';
    iconLeft?: JSX.Element;
    iconRight?: JSX.Element;
    variant?: ButtonVariant;
    size?: ButtonSize;
    titleStyle?: StyleProp<TextStyle>;
    active?: boolean;
    isLoading?: boolean;
    block?: boolean;
    children?: React.ReactNode;
    numberOfLines?: number;
    iconLeftStyle?: StyleProp<ViewStyle>;
    iconRightStyle?: StyleProp<ViewStyle>;
}

export const Button: FC<ButtonProps> = ({
    active,
    alignTitle = 'center',
    block,
    children,
    disabled,
    iconLeft,
    iconLeftStyle,
    iconRight,
    iconRightStyle,
    isLoading,
    size = Platform.select({ web: 'medium', default: 'large' }),
    style,
    title: propTitle,
    titleStyle,
    variant = 'primary',
    numberOfLines,
    ...btnProps
}) => {
    const { isHovered, hoverEventHandlers } = useHoverState();
    const { buttonStyle, textStyle, underlayColor } = variantStyleMap[variant]({ isHovered, isActive: active });

    const title = useMemo(() => {
        if (Children.count(children) === 0 || typeof children === 'string') {
            return (
                <Typography
                    style={[
                        styles.text,
                        size === 'large' && styles.textLarge,
                        textStyle,
                        disabled && styles.disabledText,
                        alignTitle === 'center' && styles.textCenter,
                        {
                            opacity: isLoading ? 0 : 1,
                        },
                        titleStyle,
                    ]}
                    numberOfLines={numberOfLines}
                >
                    {propTitle || children}
                </Typography>
            );
        }

        return children;
    }, [children, size, textStyle, disabled, alignTitle, isLoading, titleStyle, numberOfLines, propTitle]);

    return (
        <TouchableHighlight
            {...hoverEventHandlers}
            underlayColor={underlayColor}
            activeOpacity={1}
            accessibilityRole="button"
            style={[
                styles.base,
                alignTitle === 'center' && styles.center,
                styles[size],
                buttonStyle,
                disabled && styles.disabled,
                block && styles.block,
                style,
            ]}
            disabled={disabled}
            {...btnProps}
        >
            <>
                {iconLeft && <View style={[styles.iconLeft, iconLeftStyle]}>{iconLeft}</View>}

                {isLoading && <LoadingCircle style={{ position: 'absolute' }} />}

                {title}

                {iconRight && <View style={[styles.iconRight, iconRightStyle]}>{iconRight}</View>}
            </>
        </TouchableHighlight>
    );
};
